import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { Button, Form, Spin } from 'antd';
import { Box } from '@mui/material';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { useLocation } from 'react-router-dom';
// import CardAccordion from '../CardAccordion';
import LeaseTable from '../../leaseRecodTable';
import { useGetQueryV3Mutation } from 'store/features/api/graphSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useSendControlDataMutation } from 'store/features/api/apiSlice';
import { hasGlKey } from '../LeaseUtils/helper';
// import StatusLegend from '../LeaseUtils/StatusLegend';
import StatusFilter, { statusConfig } from '../LeaseUtils/StatusLegend';

dayjs.extend(utc);
const allPossibleStatuses = Object.keys(statusConfig);
const LeaseSchedule = forwardRef(({ screen, viewData, dataFullyLoaded = false }, ref) => {
	const statusData = useMemo(() => {
		return screen?.toLowerCase() === 'view'
			? Object.keys(viewData?.parkedData ?? {})?.length === 0
				? viewData?.data
				: viewData?.parkedData
			: [];
	}, [viewData, screen]);

	const hasGlAssigned = hasGlKey(statusData);
	const hasCCAssigned = Boolean(statusData?.refCCAssignmentIdentityId);
	const form = Form.useFormInstance();
	const [hasLoadedData, setHasLoadedData] = useState(false);
	const [getLeaseTableData, { data: leaseControlDataV3, isLoading: isLeaseTableDataLoading }] = useGetQueryV3Mutation();
	const [isClicked, setIsClicked] = useState(false);
	const [getLeaseData, { data: leaseControlData, isLoading: isLeaseControlDataLoading }] = useSendControlDataMutation();
	const [flag, setFlag] = useState({
		check: 0,
		editable: 0,
	});

	const dispatch = useDispatch();

	const [selectedStatuses, setSelectedStatuses] = useState([]);
	const isV3DataActive = useMemo(
		() => (screen?.toLowerCase() === 'view' || flag?.check === 0) && viewData?.activationStatus?.toLowerCase() === 'active',
		[screen, flag, viewData]
	);

	const { state } = useLocation();

	const { creatorOrganizationId } = useSelector((state) => state.profile.profileData);
	const companyCode = creatorOrganizationId;
	const queryV3Loader = async (force = false) => {
		let result;
		if (screen !== 'CREATE' && (!hasLoadedData || force)) {
			const version = String(viewData?.data?.version);
			if (viewData?.activationStatus?.toLowerCase() !== 'active') {
				await handleDataLoading();
				return;
			}

			if (
				version && version !== 'null' && version !=="undefined" &&
				companyCode
				// &&
				// (viewData?.activationStatus?.toLowerCase() === 'active' ||
				// 	viewData?.activationStatus?.toLowerCase() === 'closed' ||
				// 	viewData?.activationStatus?.toLowerCase() === 're-open')
			) {
				 result = await getLeaseTableData({
					companyCode,
					fieldCodes: ['companyCode', 'identityRecordIdString', 'version'],
					fieldValues: [companyCode, state._id, version],
					name: 'leaseSchedulesOnVersion-v6',
				})

				// dispatch
			}
			setHasLoadedData(true);
			return result;

		}
	};
	const memoizedLeaseControlData = useMemo(() => {
		if (isV3DataActive) {
			return leaseControlDataV3?.results ?? [];
		}
		return leaseControlData?.responseData ?? [];
	}, [isV3DataActive, leaseControlDataV3, leaseControlData]);
	// useEffect(() => {
	// 	console.log('🚀 ~ LeaseSchedule ~ memoizedLeaseControlData:', memoizedLeaseControlData);
	// }, [memoizedLeaseControlData]);

	// const dynamicStatusList = useMemo(() => {
	// 	if (!isV3DataActive || !Array.isArray(memoizedLeaseControlData)) {
	// 		return [];
	// 	}
	// 	// Use a Set to get unique processStatus values from the data
	// 	const statuses = new Set(memoizedLeaseControlData.map(item => item.processStatus).filter(Boolean));
	// 	return Array.from(statuses);
	// }, [isV3DataActive, memoizedLeaseControlData]);
	const handleStatusFilterChange = (clickedStatus) => {
		setSelectedStatuses((prevStatuses) => {
			if (prevStatuses.includes(clickedStatus)) {
				return prevStatuses.filter((s) => s !== clickedStatus); // Deselect
			}
			return [...prevStatuses, clickedStatus]; // Select
		});
	};
	const filteredDataForTable = useMemo(() => {
		if (!isV3DataActive || selectedStatuses.length === 0) {
			return memoizedLeaseControlData;
		}

		// Check if "Unprocessed" is one of the selected filters
		const isUnprocessedSelected = selectedStatuses.includes('Unprocessed');

		return memoizedLeaseControlData.filter((item) => {
			const itemStatus = item.processStatus;

			// If "Unprocessed" is selected, include items where status is empty, null, or undefined
			if (isUnprocessedSelected && (itemStatus === '' || itemStatus === null || itemStatus === undefined)) {
				return true;
			}

			// For all other statuses, check for a direct match
			return selectedStatuses.includes(itemStatus);
		});
	}, [isV3DataActive, memoizedLeaseControlData, selectedStatuses]);
	useEffect(() => {
		setSelectedStatuses([]);
	}, [isV3DataActive]);
	const handleDataLoading = async () => {
		setIsClicked(true);
		let payloadData = {};
		const formData = form.getFieldsValue();
		// console.log('🚀 ~ handleDataLoading ~ formData:', formData);

		if (screen?.toLowerCase() === 'create' || (screen?.toLowerCase() === 'revise' && formData?.modificationSequence === 0)) {
			payloadData = {
				advanceAmount: formData?.advanceAmount,
				leaseTerm: formData?.leaseTerm,
				discountRate: formData?.initialDiscountRate,
				startYear: String(formData?.leaseStartDate?.year()),
				escalation: 0,
				startMonth: String(formData?.leaseStartDate?.month() + 1),
				paymentFrequency: formData?.paymentFrequency,
				paymentTime: formData?.paymentModality,
				advanceDuration: formData?.advanceAmounts?.filter((item) => item !== 0)?.length,
				startDay: String(formData?.leaseStartDate?.date()),
				upfrontLeasePayment: String(formData?.upfrontLeasePayment),
				initialDirectCost: String(formData?.initialDirectCost),
				securityDeposit: String(formData?.securityDeposit),
				endOfFinancialYear: formData?.endOfFinancialYear,
				advanceAmounts: formData?.advanceAmounts,
				grossRents: formData?.grossRents,
				grossRent: 0,
				changes: [],
				cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
			};
			const convertJson = JSON.stringify(payloadData);
			// console.log(payloadData, 'payloadData4455');
			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};
			try {
				await getLeaseData(formattedPayload).unwrap();
			} catch (error) {
				return;
			}
		} else {
			if (dataFullyLoaded) {
				// const currentChangesLength = form.getFieldValue('changes')?.length;
				const effectiveChanges = form.getFieldValue('changes')?.filter((_, index) => index !== 0);

				// Normalize array lengths by ensuring inputChangeAdvanceAmounts has same length as inputChangeGrossRents
				const normalizedEffectiveChanges = effectiveChanges.map((item) => {
					const grossRentsLength = item?.inputChangeGrossRents?.length || 0;
					const advanceAmountsLength = item?.inputChangeAdvanceAmounts?.length || 0;

					// If advanceAmounts array is shorter than grossRents array, pad with zeros
					if (grossRentsLength > advanceAmountsLength && Array.isArray(item?.inputChangeGrossRents)) {
						const paddedAdvanceAmounts = [...(item?.inputChangeAdvanceAmounts || [])];
						// Add zeros to match the length
						while (paddedAdvanceAmounts.length < grossRentsLength) {
							paddedAdvanceAmounts.push(0);
						}
						return {
							...item,
							inputChangeAdvanceAmounts: paddedAdvanceAmounts,
						};
					}
					return item;
				});

				payloadData = {
					advanceAmount: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.reduce(
						(accumulator, currentValue) => accumulator + currentValue,
						0
					),
					leaseTerm: formData?.changes?.[0]?.inputChangeLeaseTerm,
					discountRate: formData?.changes?.[0]?.inputChangeDiscountRate,
					startDay: String(formData?.leaseStartDate?.date()),
					startMonth: String(formData?.leaseStartDate?.month() + 1),
					startYear: String(formData?.leaseStartDate?.year()),
					escalation: 0,
					paymentFrequency: formData?.changes?.[0]?.paymentFrequency,
					paymentTime: formData?.changes?.[0]?.paymentTime,
					advanceDuration: formData?.changes?.[0]?.inputChangeAdvanceAmounts?.filter((item) => item !== 0)?.length,
					upfrontLeasePayment: formData?.changes?.[0]?.upfrontLeasePayment,
					initialDirectCost: formData?.changes?.[0]?.initialDirectCost,
					securityDeposit: formData?.securityDeposit,
					endOfFinancialYear: formData?.endOfFinancialYear,
					advanceAmounts: formData?.changes?.[0]?.inputChangeAdvanceAmounts,
					grossRents: formData?.changes?.[0]?.inputChangeGrossRents,
					cashIncentiveAmount: formData?.cashIncentiveAmount ?? 0,
					changes: normalizedEffectiveChanges.map((item, index) => {
						// console.log('🚀 ~ handleDataLoading changes:normalizedEffectiveChanges.map ~ item:', item);
						if (index === normalizedEffectiveChanges.length - 1) {
							return {
								...item,
								inputChangeAdvance: formData?.advanceAmounts?.reduce((acc, curr) => acc + curr, 0),
								inputChangeAdvanceAmounts: formData?.advanceAmounts,
								inputChangeGrossRents: formData?.grossRents,
								upfrontLeasePayment: String(formData?.upfrontLeasePayment),
								initialDirectCost: String(formData?.initialDirectCost),
								securityDeposit: String(formData?.securityDeposit),
								cashIncentiveAmount: String(formData?.cashIncentiveAmount ?? 0),
								inputChangeDiscountRate: formData?.initialDiscountRate,
							};
						}
						return {
							...item,
						};
					}),
					grossRent: 0,
				};

				if (
					Array.isArray(payloadData.grossRents) &&
					Array.isArray(payloadData.advanceAmounts) &&
					payloadData.grossRents.length > payloadData.advanceAmounts.length
				) {
					const paddedAdvanceAmounts = [...payloadData.advanceAmounts];
					while (paddedAdvanceAmounts.length < payloadData.grossRents.length) {
						paddedAdvanceAmounts.push(0);
					}
					payloadData.advanceAmounts = paddedAdvanceAmounts;
				}
			}

			const convertJson = JSON.stringify(payloadData);

			const convertBase64 = btoa(convertJson);
			const formattedPayload = {
				data: convertBase64,
				processCode: state?.processCode,
				scriptName: 'lease-controller-lease',
				payloadData: payloadData,
			};

			try {
				await getLeaseData(formattedPayload).unwrap();
			} catch (error) {
				console.error('Error in data loading:', error);
			}
		}
		// console.log(payloadData, 'payloadData123');
	};
	useImperativeHandle(ref, () => ({
		handleDataLoading,
		flag,
		setFlag,
		queryV3Loader,
	}));
	useEffect(() => {
		setIsClicked(false);
	}, [screen, viewData?.data?.leaseId]);

	// const statusColorMap = {
	// 	Pending: '#E6F0FF', // light blue
	// 	Unprocessed: '#E6F0FF', // light blue
	// 	Reversed: '#ffcccc', // light red
	// 	Returned: '#FFFDE6', // light yellow
	// 	Recorded: '#E6FFE6', // light green
	// 	Processed: '#E6F0FF', // light blue
	// };
	return (
		<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '4px', pt: '15px', flexDirection: 'column' }}>
			<Spin spinning={isLeaseControlDataLoading}>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						gap: '4px',
						pt: '15px',
						flexDirection: 'row',
					}}
				>
					{screen !== 'VIEW' && (
						<>
							{/* Left side buttons */}
							<Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
								<Button
									type="primary"
									onClick={() => {
										setFlag({ ...flag, check: 1 });
										handleDataLoading();
									}}
									style={{ marginBottom: 10, marginTop: 10 }}
									loading={isLeaseControlDataLoading}
								>
									{screen === 'CREATE' ? 'Run Lease Schedule' : 'Update Lease Schedule'}
								</Button>
							</Box>

							{/* Right side button */}
						</>
					)}
				</Box>
				<Spin spinning={isLeaseTableDataLoading || isLeaseControlDataLoading}>
					{memoizedLeaseControlData?.length > 0 && (
						// <CardAccordion
						// 	title="Lease Schedule"
						// 	customHeaderSX={{
						// 		fontSize: '16px',
						// 		backgroundColor: '#e1f0ff !important',
						// 		p: '10px',
						// 	}}
						// 	typographySX={{ fontSize: '16px !important' }}
						// 	darkTitle={true}
						// >
						<>
							{isV3DataActive && (
								<StatusFilter
									statusesToRender={allPossibleStatuses}
									selectedStatuses={selectedStatuses}
									onFilterChange={handleStatusFilterChange}
								/>
							)}
							<LeaseTable
								// leaseControlData={memoizedLeaseControlData}
								leaseControlData={filteredDataForTable}
								screen={screen}
								queryV3Loader={queryV3Loader}
								isClicked={isClicked}
								hasGlAssigned={hasGlAssigned}
								hasCCAssigned={hasCCAssigned}
							/>
						</>
						// </CardAccordion>
					)}
				</Spin>
			</Spin>
		</Box>
	);
});

LeaseSchedule.displayName = 'LeaseSchedule';

export default LeaseSchedule;
